import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Chart, registerables } from 'chart.js';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { sk } from 'date-fns/locale';
import configApi from '../../config';

registerLocale('sk', sk);
Chart.register(...registerables);

const RevenueByDayChart = () => {
    const chartRef = useRef(null);
    const [chartInstance, setChartInstance] = useState(null);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const token = localStorage.getItem("token");
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };

    const handleWeekChange = (date) => {
        if (!date) return;
        const day = date.getDay();
        // Calculate Monday (if day is 0/Sunday, go back 6 days, else go back day-1)
        const diff = date.getDate() - day + (day === 0 ? -6 : 1);
        const firstDay = new Date(new Date(date).setDate(diff));
        firstDay.setHours(0, 0, 0, 0);

        const lastDay = new Date(firstDay);
        lastDay.setDate(lastDay.getDate() + 6);
        lastDay.setHours(23, 59, 59, 999);

        setStartDate(firstDay);
        setEndDate(lastDay);
    };

    const fetchRevenueData = async () => {
        try {
            const response = await axios.get(
                `${configApi.apiEndpoint}/api/Stats/RevenueByDay`,
                {
                    ...config,
                    params: {
                        startDate: startDate ? startDate.toISOString() : null,
                        endDate: endDate ? endDate.toISOString() : null
                    }
                }
            );
            const safeData = Array.isArray(response.data) ? response.data : [];
            createChart(safeData);
        } catch (error) {
            console.error('Chyba pri načítavaní príjmov podľa dňa:', error);
        }
    };

    const createChart = (data) => {
        const labels = data.map(item => item.day);
        const revenues = data.map(item => item.revenue);

        const ctx = chartRef.current.getContext('2d');
        const gradient = ctx.createLinearGradient(0, 0, 0, 300);
        gradient.addColorStop(0, 'rgba(46, 204, 113, 0.8)'); // Deep emerald green
        gradient.addColorStop(1, 'rgba(46, 204, 113, 0.2)');

        const chartConfig = {
            type: 'bar',
            data: {
                labels: labels,
                datasets: [{
                    label: 'Historický hrubý zisk (€)',
                    data: revenues,
                    backgroundColor: gradient,
                    borderColor: '#2ecc71',
                    borderWidth: 1,
                    borderRadius: 4,
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    y: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Zisk (€)'
                        },
                        ticks: {
                            callback: function (value) {
                                return new Intl.NumberFormat('sk-SK').format(value) + ' €';
                            }
                        }
                    },
                    x: {
                        title: {
                            display: false
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    title: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: function (context) {
                                let label = context.dataset.label || '';
                                if (label) {
                                    label += ': ';
                                }
                                if (context.parsed.y !== null) {
                                    label += new Intl.NumberFormat('sk-SK').format(context.parsed.y) + ' €';
                                }
                                return label;
                            }
                        }
                    }
                }
            }
        };

        if (chartRef.current) {
            const existingChart = Chart.getChart(chartRef.current);
            if (existingChart) {
                existingChart.destroy();
            }
        }

        const newChartInstance = new Chart(ctx, chartConfig);
        setChartInstance(newChartInstance);
    };

    useEffect(() => {
        handleWeekChange(new Date()); // Initialize to current week
    }, []);

    useEffect(() => {
        if (startDate && endDate) {
            fetchRevenueData();
        }
    }, [startDate, endDate]);

    return (
        <div className="card-custom h-100">
            <div className="card-custom-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h5 className="m-0">Celkový zisk podľa dní</h5>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <DatePicker
                        className="form-control form-control-sm rounded-pill text-center shadow-sm border-0 px-3"
                        style={{ minWidth: '220px' }}
                        selected={startDate}
                        onChange={handleWeekChange}
                        startDate={startDate}
                        endDate={endDate}
                        locale="sk"
                        calendarStartDay={1}
                        value={`${startDate?.toLocaleDateString('sk-SK')} - ${endDate?.toLocaleDateString('sk-SK')}`}
                    />
                    <button className="btn btn-sm btn-outline-primary rounded-pill px-3 shadow-sm border-2 fw-bold" onClick={fetchRevenueData}>
                        Obnoviť
                    </button>
                </div>
            </div>
            <div style={{ height: '300px' }}>
                <canvas ref={chartRef}></canvas>
            </div>
        </div>
    );
};

export default RevenueByDayChart;
